/**
 * AssetManagement Routes
 * Main router for the Asset Management domain (mounted at /app/assetmanagement/*).
 *
 * Two audiences:
 *   - STAFF (Staff / AssetReader): dashboard, my-assets, scan, read-only detail.
 *   - ADMIN (AssetAdmin / Admin): the register, operations and administration —
 *     every admin route is wrapped in <ProtectedRoute requiredRoles={ASSET_ADMIN_ROLES}>.
 */

import { Routes, Route, Navigate } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { Loader, Center } from '@mantine/core';
import MainLayout from '../../../layouts/MainLayout';
import { ProtectedRoute } from '@/components/ccms-components/auth/components/ProtectedRoute';
import { ASSET_ADMIN_ROLES } from './components/constants';

import AssetStaffDashboard from './dashboard';
const MyAssetsPage = lazy(() => import('./my-assets'));
const ScanToFindPage = lazy(() => import('./scan'));
const AssetListPage = lazy(() => import('./list'));
const AssetDetailPage = lazy(() => import('./view'));
const AssetBulkImportPage = lazy(() => import('./BulkImport'));
const AssetOverviewPage = lazy(() => import('./admin/overview'));
const AssetTransfersPage = lazy(() => import('./admin/transfers'));
const AssetReportsPage = lazy(() => import('./admin/reports'));
const AssetSettingsPage = lazy(() => import('./admin/settings'));
const AssetLookupsPage = lazy(() => import('./admin/lookups'));

const LoadingFallback = () => (
  <Center h={400}><Loader size="lg" /></Center>
);

const admin = (el: React.ReactNode) => (
  <ProtectedRoute requiredRoles={ASSET_ADMIN_ROLES}>{el}</ProtectedRoute>
);

export default function AssetManagementRoutes() {
  return (
    <MainLayout domainKey="assetmanagement">
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          {/* ---------- STAFF (all asset users) ---------- */}
          <Route path="/" element={<AssetStaffDashboard />} />
          <Route path="/my-assets" element={<MyAssetsPage />} />
          <Route path="/scan" element={<ScanToFindPage />} />
          {/* Detail is shared; the page itself gates write actions by role. */}
          <Route path="/view/:id" element={<AssetDetailPage />} />

          {/* ---------- ADMIN (AssetAdmin / Admin) ---------- */}
          <Route path="/admin/overview" element={admin(<AssetOverviewPage />)} />
          <Route path="/list" element={admin(<AssetListPage />)} />
          <Route path="/admin/transfers" element={admin(<AssetTransfersPage />)} />
          <Route path="/admin/reports" element={admin(<AssetReportsPage />)} />
          <Route path="/bulk-import" element={admin(<AssetBulkImportPage />)} />
          <Route path="/admin/settings" element={admin(<AssetSettingsPage />)} />
          <Route path="/admin/lookups/*" element={admin(<AssetLookupsPage />)} />

          <Route path="*" element={<Navigate to="/app/assetmanagement" replace />} />
        </Routes>
      </Suspense>
    </MainLayout>
  );
}
